import React, { useState, useEffect, useRef } from 'react';
import socket from '../services/socket';
import { useProMode } from '../context/ProModeContext';
import { API_BASE_URL } from '../config';
import '../styles/Events.css';
const Events = () => {
  const { isProMode } = useProMode();
  const [events, setEvents] = useState([]);
  const [trades, setTrades] = useState([]); // Separate state for trades
  const [activeEvent, setActiveEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('recent'); // 'recent', 'all', 'impact'
  const [showTrades, setShowTrades] = useState(true); // Toggle for trades panel
  const eventsEndRef = useRef(null);
  const tradesEndRef = useRef(null);
  const [stocks, setStocks] = useState({}); // Map of stock IDs to ticker symbols
  const initialLoadRef = useRef(true); // Track if this is the initial load

  // Terminal specific state
  const [terminalCommand, setTerminalCommand] = useState('');
  const [commandHistory, setCommandHistory] = useState([]);
  const [showTerminal, setShowTerminal] = useState(isProMode);

  // Fetch stocks for ticker lookup
  useEffect(() => {
    const fetchStocks = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/stocks`);
        if (response.ok) {
          const stocksData = await response.json();
          const stockMap = {};
          stocksData.forEach(stock => {
            stockMap[stock.id] = stock.ticker;
          });
          setStocks(stockMap);
        }
      } catch (error) {
        console.error('Error fetching stocks:', error);
      }
    };

    fetchStocks();
  }, []);

  // Fetch event history on mount + poll every 15s as fallback
  useEffect(() => {
    const fetchEventHistory = async (isInitial) => {
      try {
        if (isInitial) setLoading(true);
        const response = await fetch(`${API_BASE_URL}/api/events?limit=500`);

        if (!response.ok) {
          throw new Error(`HTTP error ${response.status}`);
        }

        const data = await response.json();
        setEvents(data);
        if (isInitial) {
          setTimeout(() => {
            initialLoadRef.current = false;
          }, 100);
        }
      } catch (error) {
        console.error('Error fetching event history:', error);
      } finally {
        if (isInitial) setLoading(false);
      }
    };

    fetchEventHistory(true);
    const interval = setInterval(() => fetchEventHistory(false), 15000);
    return () => clearInterval(interval);
  }, []);
  
  // Fetch recent trades on mount
  useEffect(() => {
    const fetchTrades = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/trades/recent?limit=100`);
        if (response.ok) {
          const data = await response.json();
          const arr = Array.isArray(data) ? data : (data.trades || []);
          setTrades(arr.map(trade => ({
            id: trade.id || `trade-${trade.timestamp}`,
            type: 'trade',
            description: trade.tradeType === 'TRADE_CLOSE'
              ? `${trade.type} position on ${trade.ticker} closed`
              : `${trade.shares} shares of ${trade.ticker} ${trade.type === 'LONG' ? 'bought LONG' : 'shorted'} @ $${parseFloat(trade.price)?.toFixed(2)}`,
            timestamp: trade.timestamp,
            tradeType: trade.type,
            userId: trade.userId,
            username: trade.username || 'Unknown',
            stockTicker: trade.ticker,
            shares: trade.shares,
            price: trade.price,
          })));
        }
      } catch (error) {
        console.error('Error fetching trades:', error);
      }
    };
    fetchTrades();
    const pollInterval = setInterval(fetchTrades, 5000);
    return () => clearInterval(pollInterval);
  }, []);

  // Listen for market events
  useEffect(() => {
    const handleMarketEvent = (event) => {
      // Add new event to the list
      setEvents(prevEvents => [event, ...prevEvents].slice(0, 500)); // Keep latest 500 events

      // Set as active event for animation
      setActiveEvent(event);

      // Add to terminal history if in pro mode
      if (isProMode) {
        const timestamp = new Date().toLocaleTimeString();
        setCommandHistory(prev => [...prev, `[${timestamp}] ALERT: ${event.description}`]);
      }

      // Clear active event after animation duration
      setTimeout(() => {
        setActiveEvent(null);
      }, 8000); // Animation lasts 8 seconds
    };

    const handleTradeExecuted = (trade) => {
      // Convert trade to event format
      const stockTicker = stocks[trade.stockId] || trade.stockId;
      let description = '';

      if (trade.type === 'LONG') {
        description = `${trade.shares} shares of ${stockTicker} bought LONG @ $${trade.price?.toFixed(2)}`;
      } else if (trade.type === 'SHORT') {
        description = `${trade.shares} shares of ${stockTicker} shorted @ $${trade.price?.toFixed(2)}`;
      } else if (trade.type === 'CLOSE_LONG') {
        const plText = trade.profitLoss >= 0 ? `+$${trade.profitLoss?.toFixed(2)}` : `-$${Math.abs(trade.profitLoss)?.toFixed(2)}`;
        description = `LONG position on ${stockTicker} closed (${plText})`;
      } else if (trade.type === 'CLOSE_SHORT') {
        const plText = trade.profitLoss >= 0 ? `+$${trade.profitLoss?.toFixed(2)}` : `-$${Math.abs(trade.profitLoss)?.toFixed(2)}`;
        description = `SHORT position on ${stockTicker} closed (${plText})`;
      }

      const tradeEvent = {
        id: `trade-${trade.timestamp}`,
        type: 'trade',
        description: description,
        timestamp: trade.timestamp || new Date().toISOString(),
        affectedStocks: [trade.stockId],
        tradeType: trade.type,
        userId: trade.userId,
        username: trade.username || 'Unknown',
        stockTicker: stockTicker,
        shares: trade.shares,
        price: trade.price,
        profitLoss: trade.profitLoss
      };

      // Add only to trades (dedicated trade module), NOT to general events
      setTrades(prevTrades => [tradeEvent, ...prevTrades].slice(0, 100)); // Keep last 100 trades
    };

    // Subscribe to socket events
    socket.on('market-event', handleMarketEvent);
    socket.on('trade-executed', handleTradeExecuted);

    return () => {
      // Cleanup listener on unmount
      socket.off('market-event', handleMarketEvent);
      socket.off('trade-executed', handleTradeExecuted);
    };
  }, [isProMode, stocks]);
  
  // Auto-scroll removed — events are newest-first so latest is already at top
  
  // Toggle terminal visibility
  useEffect(() => {
    setShowTerminal(isProMode);
  }, [isProMode]);
  
  // Filter events based on viewMode
  const filteredEvents = React.useMemo(() => {
    switch(viewMode) {
      case 'all':
        return [...events];
      case 'impact':
        return events.filter(event => event.multiplier && event.multiplier !== 1);
      case 'recent':
      default:
        return events.slice(0, 20); // Show last 20 in recent mode
    }
  }, [events, viewMode]);
  
  // Handle terminal commands
  const handleTerminalSubmit = (e) => {
    e.preventDefault();
    
    if (!terminalCommand.trim()) return;
    
    const timestamp = new Date().toLocaleTimeString();
    const cmdLine = `[${timestamp}] > ${terminalCommand}`;
    
    setCommandHistory(prev => [...prev, cmdLine]);
    
    // Process commands
    const cmd = terminalCommand.trim().toUpperCase();
    
    if (cmd === 'CLEAR') {
      setCommandHistory([]);
    } else if (cmd === 'VIEW ALL') {
      setViewMode('all');
      setCommandHistory(prev => [...prev, `[${timestamp}] VIEWING ALL EVENTS`]);
    } else if (cmd === 'VIEW RECENT') {
      setViewMode('recent');
      setCommandHistory(prev => [...prev, `[${timestamp}] VIEWING RECENT EVENTS`]);
    } else if (cmd === 'VIEW IMPACT') {
      setViewMode('impact');
      setCommandHistory(prev => [...prev, `[${timestamp}] VIEWING HIGH IMPACT EVENTS`]);
    } else if (cmd === 'HELP') {
      setCommandHistory(prev => [...prev, 
        `[${timestamp}] AVAILABLE COMMANDS:`,
        `CLEAR - Clear terminal history`,
        `VIEW ALL - Show all events`,
        `VIEW RECENT - Show recent events only`,
        `VIEW IMPACT - Show high impact events only`,
        `HELP - Show this help message`
      ]);
    } else {
      setCommandHistory(prev => [...prev, `[${timestamp}] UNKNOWN COMMAND: ${terminalCommand}`]);
    }
    
    setTerminalCommand('');
  };
  
  // Render terminal
  const renderTerminal = () => {
    if (!isProMode) return null;
    
    return (
      <div className="pro-terminal event-terminal">
        {/* <div className="terminal-header">
          <span>EVENT MONITOR</span>
          <button 
            className="terminal-toggle"
            onClick={() => setShowTerminal(!showTerminal)}
          >
            {showTerminal ? 'HIDE' : 'SHOW'}
          </button>
        </div> */}
      </div>
    );
  };
  
  // Render event as a compact table row
  const renderEventItem = (event, index) => {
    const eventDate = new Date(event.timestamp);
    const formattedTime = eventDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

    // Get event title
    let eventTitle = '';
    if (event.type === 'trade') {
      if (event.tradeType === 'LONG') eventTitle = 'LONG';
      else if (event.tradeType === 'SHORT') eventTitle = 'SHORT';
      else if (event.tradeType === 'CLOSE_LONG') eventTitle = 'CLOSE LONG';
      else if (event.tradeType === 'CLOSE_SHORT') eventTitle = 'CLOSE SHORT';
      else eventTitle = 'TRADE';
    } else {
      eventTitle = event.type ? event.type.toUpperCase().replace(/_/g, ' ') : 'EVENT';
    }

    // Convert affected stocks from IDs to tickers
    let affectedDisplay = 'ALL';
    if (event.affectedStocks) {
      if (Array.isArray(event.affectedStocks) && event.affectedStocks.length > 0) {
        const tickers = event.affectedStocks.map(stockId => stocks[stockId] || stockId);
        affectedDisplay = tickers.join(', ');
      } else if (typeof event.affectedStocks === 'string' && event.affectedStocks !== 'ALL') {
        affectedDisplay = stocks[event.affectedStocks] || event.affectedStocks;
      }
    }

    // Format impact
    let impactDisplay = '-';
    let impactClass = '';
    if (event.multiplier) {
      const pct = (event.multiplier - 1) * 100;
      impactDisplay = pct >= 0 ? `+${pct.toFixed(0)}%` : `${pct.toFixed(0)}%`;
      impactClass = pct >= 0 ? 'positive' : 'negative';
    }

    return (
      <tr key={event.id || event.timestamp + index} className={`event-row ${event.type?.toLowerCase() || ''}`}>
        <td className="event-time-col">{formattedTime}</td>
        <td className="event-type-col">{eventTitle}</td>
        <td className="event-desc-col">{event.description}</td>
        <td className={`event-impact-col ${impactClass}`}>{impactDisplay}</td>
        <td className="event-affected-col">{affectedDisplay}</td>
      </tr>
    );
  };
  
  return (
    <div className={`events-container ${isProMode ? 'pro-events' : ''}`}>
      {/* Active Event Animation */}
      {activeEvent && (
        <div className="active-event">
          <div className="event-animation">
            <div className="event-content">
              <h3>{isProMode ? "MARKET ALERT" : "Market Alert"}</h3>
              <p>{isProMode ? activeEvent.description.toUpperCase() : activeEvent.description}</p>
              <div className="event-time">
                {isProMode ?
                  `[${new Date(activeEvent.timestamp).toISOString().replace('T', ' ').substring(0, 19)}]` :
                  new Date(activeEvent.timestamp).toLocaleTimeString()
                }
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Event History */}
      <div className="events-history">
        <h3>{isProMode ? "MARKET EVENTS LOG" : "Recent Market Events"}</h3>
        
        {loading ? (
          <div className="loading-events">
            <div className="spinner"></div>
            <p>{isProMode ? "LOADING MARKET EVENTS..." : "Loading market events..."}</p>
          </div>
        ) : events.length === 0 ? (
          <div className="no-events">
            <p>{isProMode ? "NO MARKET EVENTS FOUND" : "No market events yet. Stay tuned!"}</p>
          </div>
        ) : (
          <div className="events-table-container">
            <table className="events-table">
              <thead>
                <tr>
                  <th>TIME</th>
                  <th>TYPE</th>
                  <th>DESCRIPTION</th>
                  <th>IMPACT</th>
                  <th>STOCKS</th>
                </tr>
              </thead>
              <tbody>
                {filteredEvents.map((event, index) => renderEventItem(event, index))}
              </tbody>
            </table>
            <div ref={eventsEndRef} />
          </div>
        )}
      </div>

      {/* Live Trades Module */}
      <div className="trades-module">
        <div className="module-header">
          <h3>{isProMode ? "LIVE TRADES" : "Live Trades"}</h3>
          <span className="trade-count">{trades.length} trades</span>
        </div>
        <div className="trades-list">
          {trades.length === 0 ? (
            <div className="no-trades">
              <p>{isProMode ? "NO TRADES YET" : "No trades yet"}</p>
            </div>
          ) : (
            trades.slice(0, 20).map((trade, index) => (
              <div key={trade.id || index} className={`trade-item ${trade.tradeType?.toLowerCase()}`}>
                <div className="trade-header">
                  <span className="trade-type">
                    {trade.tradeType === 'LONG' && '📈 LONG'}
                    {trade.tradeType === 'SHORT' && '📉 SHORT'}
                    {trade.tradeType === 'CLOSE_LONG' && '💰 CLOSE LONG'}
                    {trade.tradeType === 'CLOSE_SHORT' && '💰 CLOSE SHORT'}
                  </span>
                  <span className="trade-time">
                    {new Date(trade.timestamp).toLocaleTimeString()}
                  </span>
                </div>
                <div className="trade-body">
                  <div className="trade-info">
                    {trade.username && (
                      <a
                        href={`/user/${trade.userId}`}
                        className="trade-user-link"
                        onClick={(e) => {
                          e.preventDefault();
                          window.location.href = `/user/${trade.userId}`;
                        }}
                      >
                        👤 {trade.username}
                      </a>
                    )}
                    <a
                      href={`/stock/${trade.affectedStocks?.[0] || trade.stockId}`}
                      className="trade-stock-link"
                      onClick={(e) => {
                        e.preventDefault();
                        window.location.href = `/stock/${trade.affectedStocks?.[0] || trade.stockId}`;
                      }}
                    >
                      {trade.stockTicker}
                    </a>
                  </div>
                  <div className="trade-details">
                    {trade.shares && <span>{trade.shares} shares</span>}
                    {trade.price && <span>@ ${trade.price.toFixed(2)}</span>}
                    {trade.profitLoss !== undefined && (
                      <span className={trade.profitLoss >= 0 ? 'profit' : 'loss'}>
                        {trade.profitLoss >= 0 ? '+$' : '-$'}{Math.abs(trade.profitLoss).toFixed(2)}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
          <div ref={tradesEndRef} />
        </div>
      </div>
    </div>
  );
};

export default Events;